import type { ReactNode } from "react";
import type { Messages } from "../fr";

export const common: Messages["common"] = {
  network: {
    offline: "Can’t connect: check your network, then try again.",
    expired: "Your session has expired. Please log in again.",
    forbidden: "You can’t perform this action.",
    notFound: "Not found.",
    unavailable: "The service isn’t responding right now. Try again in a moment.",
    failed: "The request didn’t go through.",
  },
  time: {
    justNow: "just now",
    today: "today",
  },
  language: {
    label: "Language",
    description: "The language of the app, of Hune’s emails and of what the agent writes (reports, dossiers, messages).",
  },
  pageTitles: {
    notFound: "Page not found — Hune",
    error: "Error — Hune",
  },
  tab: {
    running: (title: string) => `Analysis in progress… — ${title}`,
  },
  states: {
    overview: "Overview",
    retry: "Try again",
    reload: "Reload the page",
    loadFailed: (subject: string) => `Couldn’t load ${subject}.`,
    loading: (subject: string) => `Loading ${subject}…`,
    loadInterrupted: "Something interrupted the loading.",
    nothingLost: "Nothing is lost: your data is still saved.",
    contact: (email: ReactNode): ReactNode[] => ["If the problem persists, write to us at ", email, "."],
    thisPage: "this page",
    yourWorkspace: "your workspace",
    notFound: {
      title: "Page not found.",
      body: "This address doesn’t lead to any screen of your workspace.",
    },
    notFoundPage: {
      title: "Page not found.",
      body: "This address doesn’t lead anywhere.",
      back: "Back to your workspace",
    },
    renderError: {
      title: "This page couldn’t be displayed.",
      body: "Something interrupted the display. Your data is untouched: reload the page to pick up where you left off.",
    },
    crash: {
      title: "This page crashed.",
      body: "Reload it. If the error comes back, let us know: we’ll fix it.",
    },
  },
  skeletons: {
    activity: (days: number) => `Activity over the last ${days} days`,
    comparedWith: (days: number) => `Compared with the previous ${days} days`,
    recentPasses: "Recent passes",
    pipeline: "Pipeline",
    leadsSource: "Fed by every pass of your monitoring",
    organization: "Organization",
    signal: "Signal",
    contact: "Contact",
    score: "Score",
    status: "Status",
    history: "History",
    pass: "Pass",
    startedAt: "Started on",
    state: "Status",
    report: "Report",
    targeting: "Targeting",
    monitoring: "Monitoring",
  },
  consent: {
    settings: "Cookie settings",
    bannerTitle: "Your cookie choices",
    bannerText:
      "Hune uses cookies required for the site to work and, with your consent, Google Analytics to measure its audience. Your choice also applies to the hune-app.com website, and can be changed at any time from “Cookie settings” (sign-in screen, account settings).",
    privacyLink: "Privacy policy",
    customize: "Customize",
    refuseAll: "Reject all",
    acceptAll: "Accept all",
    panelText:
      "Turn each category on or off. Your choice applies to the application and the site, is kept for six months and can be changed at any time.",
    necessary: "Necessary",
    necessaryText: "Signing in to your account, security, remembering the offer you’re viewing and your choices below.",
    alwaysOn: "Always on",
    internalStats: "Internal statistics",
    internalStatsText:
      "Anonymous audience measurement carried out by Hune, never shared with third parties. Exempt from consent, but you can still opt out.",
    analyticsText: "Audience measurement by Google (_ga cookies, 13 months at most). Set only with your consent.",
    save: "Save my choices",
  },
};
